import { ExternalLink } from 'lucide-react';
import { groupUseCase } from '@/lib/intakeUseCase';

// The "Use case" section of the intake Drawer: the full submission, grouped
// as the submitter answered it (see lib/intakeUseCase.js), and for the team
// its own scoring and sizing. Long answers wrap and keep their line breaks;
// nothing is truncated.

const label = 'text-[11px] uppercase tracking-wide font-bold text-[#98139C]';
const groupTitle = 'text-xs font-semibold text-[#30302F] dark:text-white';
const answer = 'text-sm text-[#30302F] dark:text-white font-light leading-relaxed whitespace-pre-line break-words';

function Answer({ row }) {
  if (row.value === null) return <span className="opacity-50">Not answered</span>;
  if (row.kind === 'list') {
    return (
      <ul className="list-disc pl-5 space-y-0.5">
        {row.value.map((x, i) => <li key={`${x}-${i}`}>{x}</li>)}
      </ul>
    );
  }
  if (row.kind === 'links') {
    return (
      <ul className="space-y-1">
        {row.value.map((l, i) => (
          <li key={`${l.url}-${i}`}>
            <a href={l.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#98139C] hover:text-[#4F0077] dark:hover:text-[#d87bdb] underline break-all">
              {l.name} <ExternalLink size={12} aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    );
  }
  return String(row.value);
}

function TeamAssessment({ assessment }) {
  const stories = assessment?.stories || [];
  if (!stories.length) return null;
  return (
    <div className="border-t border-[#e3e3e3] dark:border-white/10 pt-4" data-testid="use-case-team-assessment">
      <h4 className={groupTitle}>Team scoring and sizing</h4>
      <p className="mt-0.5 text-xs text-[#30302F]/70 dark:text-white/60">Visible to the team only.</p>
      <table className="mt-2 w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className={`${label} font-bold pb-1`}>Story</th>
            <th className={`${label} font-bold pb-1 text-right`}>Points</th>
            <th className={`${label} font-bold pb-1 text-right`}>Size</th>
            <th className={`${label} font-bold pb-1 text-right`}>RICE</th>
          </tr>
        </thead>
        <tbody>
          {stories.map((s, i) => (
            <tr key={`${s.title}-${i}`} className="border-t border-[#e3e3e3] dark:border-white/10 font-light">
              <td className="py-1 pr-2 break-words">{s.title}</td>
              <td className="py-1 text-right">{s.story_points ?? ''}</td>
              <td className="py-1 text-right">{s.t_shirt_size || ''}</td>
              <td className="py-1 text-right">{s.rice_score ?? ''}</td>
            </tr>
          ))}
        </tbody>
        {assessment.total_points != null && (
          <tfoot>
            <tr className="border-t border-[#e3e3e3] dark:border-white/10 font-semibold">
              <td className="py-1">Total</td>
              <td className="py-1 text-right">{assessment.total_points}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export default function UseCaseDetails({ item }) {
  const groups = groupUseCase(item);
  return (
    <section data-testid="request-drawer-use-case">
      <h3 className={label}>Use case</h3>
      <div className="mt-2 space-y-4">
        {groups.map((g) => (
          <div key={g.id} className="border-t border-[#e3e3e3] dark:border-white/10 pt-4" data-testid={`use-case-group-${g.id}`}>
            <h4 className={groupTitle}>{g.title}</h4>
            <dl className="mt-2 space-y-3">
              {g.rows.map((r, i) => (
                <div key={`${r.label}-${i}`}>
                  <dt className="text-xs font-medium text-[#30302F]/70 dark:text-white/60">{r.label}</dt>
                  <dd className={`${answer} mt-0.5`}><Answer row={r} /></dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
        <TeamAssessment assessment={item?.assessment} />
      </div>
    </section>
  );
}
